import {useSyncExternalStore} from 'react'
import {parseAppZoneDate} from '@/utils/dateUtils'
import {isBeforeTripStart} from './arrivalRouting'

/**
 * IS THE ARRIVAL GUIDE WORTH OFFERING YET — the window the plane button lives in.
 *
 * ── ⚠️ TWO HOURS BEFORE THE PICKUP, UNTIL THE RIDE STARTS ─────────────────────────
 * Being on the ongoing list is not on its own a reason to show the plane. A from-airport
 * booking can sit there long before anyone is going anywhere, and a plane floating over
 * every page for hours stops being an invitation and becomes furniture — the passenger
 * learns to ignore the one control that reopens the guide.
 *
 * The other end is the ride STARTING. From that moment the passenger is in the car: the
 * booth is found, the walk is done, and the guide describes a journey they have finished
 * making. `/track-driver` is the screen for that, and this is how the two stop overlapping.
 *
 * ⚠️ `Arrived` IS STILL INSIDE THE WINDOW. The driver waiting at the kerb has not started
 * the ride — the passenger may well still be walking out to them, which is precisely when
 * the guide is worth having. `isBeforeTripStart` draws that line once, for everything.
 *
 * ── ⚠️ WHY THIS TICKS, AND WHY IT HAS TO ──────────────────────────────────────────
 * Nothing broadcasts "two hours to go". The trip is polled every 2s, but react-query's
 * structural sharing hands back the SAME object when the payload has not changed — which
 * is exactly the case for a booking hours out, whose fields nobody is touching. So the
 * poll cannot be relied on to re-render at the moment the window opens: without a tick of
 * its own the button appears whenever something else on the page happens to re-render,
 * which could be minutes late or not until a navigation.
 *
 * ⚠️ IT READS THE CLOCK AS A STORE, NOT DURING RENDER — same shape as `useDelayCountdown`,
 * and for two reasons. A `Date.now()` in the body is an impure read that React's own lint
 * rejects (`react-hooks/purity`), and recomputing from the clock rather than counting down
 * means a missed tick costs nothing: an interval is throttled in a background tab and stops
 * outright on a sleeping phone, so the tick must be a repaint trigger and never the source
 * of the answer.
 */

/** How long before the scheduled pickup the guide becomes offerable. */
export const GUIDE_WINDOW_BEFORE_MS = 2 * 60 * 60 * 1000

/* A MINUTE, not a second. The boundary is a two-hour mark that nothing on screen counts
   down to, so the worst this costs is a minute of lateness on a button nobody is waiting
   for — against a re-render of the whole layout on every tick, for as long as an airport
   pickup sits on the ongoing list. */
const TICK_MS = 60_000
const subscribeToTicks = (onTick) => {
    const timer = setInterval(onTick, TICK_MS)
    return () => clearInterval(timer)
}
/** No airport ride pending — a page with no window to open should not hold a timer. */
const subscribeToNothing = () => () => {}
/* ⚠️ FLOORED TO THE TICK. `getSnapshot` must return the same value until something really
   changes; a raw `Date.now()` differs on every call and React re-renders forever chasing it. */
const ticksNow = () => Math.floor(Date.now() / TICK_MS)

/**
 * The instant the window opens, or null when the trip does not say when it is due.
 *
 * ⚠️ `parseAppZoneDate`, because `scheduled_at` is app-zone wall clock with no offset on it
 * (see utils/dateUtils). `new Date(...)` reads it as the VIEWER's local time, so a passenger
 * outside Toronto would get a window hours out of place — opening early or, worse, only
 * after their ride had already started.
 */
export function arrivalGuideWindowOpensAtMs(trip) {
    const scheduled = parseAppZoneDate(trip?.scheduled_at)
    const ms = scheduled?.getTime()
    return Number.isFinite(ms) ? ms - GUIDE_WINDOW_BEFORE_MS : null
}

/**
 * @param {object|null} trip the from-airport trip the guide is about
 * @returns {boolean} whether the guide should be offered right now
 */
export function useArrivalGuideWindow(trip) {
    const opensAt = arrivalGuideWindowOpensAtMs(trip)
    const beforeStart = !!trip && isBeforeTripStart(trip)

    /**
     * ⚠️ THE TICK IS DECIDED WITHOUT READING THE CLOCK — "there is a pickup ahead of us with
     * a time on it", not "the window has not opened yet". The tighter condition would want
     * the current time to evaluate, and the current time is the thing this call returns; the
     * subscription cannot depend on its own result. Ticking for the whole of the window
     * instead of only up to its opening costs a repaint a minute, which is the same price
     * the opening itself costs.
     */
    const pending = beforeStart && opensAt != null
    const tick = useSyncExternalStore(pending ? subscribeToTicks : subscribeToNothing, ticksNow)

    if (!beforeStart) return false
    /* ⚠️ NO PICKUP TIME, NO BUTTON — no lenient fallback here. Every trip has a
       `scheduled_at`; a null one is a broken payload, not a passenger to accommodate, and
       guessing on its behalf would put the plane on screen at times nothing can predict. */
    if (opensAt == null) return false
    /* The floored tick, back in milliseconds — see `ticksNow` for why it is floored. */
    return tick * TICK_MS >= opensAt
}

export default useArrivalGuideWindow
